import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SubmitButton,
  toast,
} from "@bleu.builders/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { serialize } from "object-to-formdata";
import { usePostHog } from "posthog-js/react";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { json, useActionData, useLocation, useNavigate } from "react-router";
import { useSearchParams, useSubmit } from "react-router-dom";
import { z } from "zod";

import { useAuthentication } from "#/contexts/AuthenticationContext";
import { client } from "#/utils/api-client";

import { LoginFormContainer } from "./Layout";

const emailSchema = z.object({
  nopassword_email_authentication: z.object({
    email: z.string().email("Email inválido"),
  }),
});

const action = async ({ request }) => {
  const formData = await request.formData();
  const { data, error, success } = await client("/login", { body: formData });

  if (!success) {
    return json({ ok: false, error: error });
  }

  return json({ ok: true, data });
};

type Login = {
  ok: boolean;
  data: {
    salt: string;
    data: string;
  };
  error: string;
};

function StepOneEmailInputForm() {
  const data = useActionData() as Login;
  const { verification, setVerification } = useAuthentication();
  const navigate = useNavigate();
  const submit = useSubmit();
  const posthog = usePostHog();
  const form = useForm({
    resolver: zodResolver(emailSchema),
  });
  const [searchParams] = useSearchParams();
  const pathname = useLocation().pathname;
  const isSignUp = pathname.includes("signup");

  const message = isSignUp
    ? "Cadastre-se agora e impulsione a sua rotina na área tributária."
    : "Preencha seu e-mail para acessar o sistema.";
  const buttonText = isSignUp ? "Comece a utilizar gratuitamente" : "Continuar";
  const verifyPath = isSignUp ? "/signup/verify" : "/login/verify";

  useEffect(() => {
    if (data === undefined) return;

    if (data.ok) {
      toast({
        title: "Verifique sua caixa de email.",
        description: "Enviamos um código de verificação.",
        variant: "default",
      });
      setVerification(data?.data);
    } else {
      toast({
        title: "Erro!",
        description: data.error,
        variant: "destructive",
      });
    }
  }, [data]);

  useEffect(() => {
    if (!verification) return;

    const redirectPath = searchParams.get("redirectTo")
      ? `?redirectTo=${searchParams.get("redirectTo")}`
      : "";

    navigate(`${verifyPath}${redirectPath}`);
  }, [verification]);

  const handleSubmit = async () => {
    const email = form.getValues("nopassword_email_authentication[email]");
    posthog.identify(email, { email });
    posthog.capture("auth:email_otp:client:user_requested_otp");

    const formData = serialize(form.getValues());
    submit(formData, { method: "post", encType: "multipart/form-data" });
  };

  return (
    <LoginFormContainer message={message}>
      <Form {...form} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="grid gap-2">
          <div className="grid gap-1">
            <FormField
              control={form.control}
              name="nopassword_email_authentication[email]"
              render={({ field: formField }) => (
                <FormItem className="w-full">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus
                      id="email"
                      type="email"
                      required
                      autoCapitalize="none"
                      autoComplete="email"
                      autoCorrect="off"
                      {...formField}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </div>
          <SubmitButton
            type="submit"
            submittingText="Carregando..."
            className="bg-orange-500 hover:bg-orange-400 h-10"
          >
            {buttonText}
          </SubmitButton>
        </div>
      </Form>
    </LoginFormContainer>
  );
}

StepOneEmailInputForm.action = action;
export { StepOneEmailInputForm };
